import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Home, Plus, MapPin, Play, Trash2, ChevronLeft, Building2, ImageUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [prop, setProp] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ unit_number: "", beds: "", baths: "", notes: "" });
  const canEdit = user?.role !== "viewer";
  const coverRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);

  const uploadCover = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/properties/${id}/cover`, fd);
      toast.success("Cover photo updated");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setCoverUploading(false); }
  };

  const load = () => api.get(`/properties/${id}`).then((r) => setProp(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const addUnit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/units", { property_id: id, ...form });
      setForm({ unit_number: "", beds: "", baths: "", notes: "" });
      setOpen(false);
      toast.success("Unit added");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const startInspection = async (unit) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.post("/inspections", { property_id: id, unit_id: unit.id, inspection_date: today });
      nav(`/app/inspections/${data.id}`);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const removeUnit = async (uid) => {
    if (!window.confirm("Delete this unit?")) return;
    await api.delete(`/units/${uid}`);
    load();
  };

  if (!prop) return <div className="py-20 text-center text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      <button onClick={() => nav("/app/properties")} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> Properties
      </button>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
        {prop.cover_media_id
          ? <img data-testid="property-cover-img" src={mediaUrl(prop.cover_media_id)} alt={prop.name} className="h-48 w-full object-cover sm:h-64" />
          : <div className="flex h-40 w-full flex-col items-center justify-center gap-2 text-slate-400 sm:h-52"><Building2 className="h-10 w-10" /><span className="text-sm font-medium">No cover photo yet</span></div>}
        {canEdit && (
          <>
            <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="cover-file-input"
              onChange={(e) => { uploadCover(e.target.files[0]); e.target.value = ""; }} />
            <button data-testid="upload-cover-btn" onClick={() => coverRef.current?.click()} disabled={coverUploading}
              className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg bg-white/95 px-4 py-2 text-sm font-semibold text-slate-700 shadow hover:bg-white disabled:opacity-60">
              <ImageUp className="h-4 w-4" /> {coverUploading ? "Uploading…" : prop.cover_media_id ? "Change cover" : "Add cover photo"}
            </button>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-900">{prop.name}</h1>
          <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{prop.address || "No address"}</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-unit-btn" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow hover:bg-blue-700">
                <Plus className="h-4 w-4" /> Add Unit
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display uppercase">New Unit</DialogTitle></DialogHeader>
              <form onSubmit={addUnit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">Unit number</label>
                  <input data-testid="unit-number-input" required value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
                    placeholder="402" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700">Beds</label>
                    <input data-testid="unit-beds-input" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })}
                      placeholder="2" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700">Baths</label>
                    <input data-testid="unit-baths-input" value={form.baths} onChange={(e) => setForm({ ...form, baths: e.target.value })}
                      placeholder="2" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="save-unit-btn" className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700">Save unit</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {prop.units.length === 0 && <p className="text-sm text-slate-500">No units yet.</p>}
        {prop.units.map((u) => (
          <div key={u.id} data-testid={`unit-card-${u.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><Home className="h-5 w-5" /></div>
              {canEdit && <button data-testid={`delete-unit-${u.id}`} onClick={() => removeUnit(u.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Unit {u.unit_number}</h3>
            <p className="text-sm text-slate-500">{u.beds || "?"} bed · {u.baths || "?"} bath</p>
            {canEdit && (
              <button data-testid={`start-inspection-${u.id}`} onClick={() => startInspection(u)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                <Play className="h-4 w-4" /> Start Inspection
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
